import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaArrowRight, FaPalette, FaPlus, FaStar, FaTimes } from 'react-icons/fa';

import {
  Aviso, Bloco, CabecalhoLinha, Celula, Chip, Chips, ColunaTamanho, Container, Legenda, LinhaEntrada, ListaCores, Resumo
} from './styles';

import Button from '../Button';
import Checkbox from '../Checkbox';
import FormInput from '../FormInput';
import Modal from '../Modal';
import { Table, TabelaContainer, Th } from '../Tabela/styles';

import { IGrade, IGradeSelecao, IPadMer } from '../../types/Grade';
import { buscarGrade, chaveVariacao, listarCores, planejarGrade } from '../../services/grade';

interface GradeProdutoProps {
  codmer?: number | string;
  /** Muda depois de gravar: recarrega a grade da API */
  recarregar: number;
  /** Grade alterada pelo usuário, ou undefined quando igual à gravada */
  onChange: (selecao: IGradeSelecao | undefined) => void;
}

// mercador.codtam tem 6 caracteres
const TAMANHO_MAXIMO = 6;

const gradeVazia: IGrade = { codbar: '', cores: [], tamanhos: [], variacoes: [] };

const igual = (a: string[], b: string[]) => a.length === b.length && a.every((valor, i) => valor === b[i]);

// Grade do produto (cor x tamanho), como a aba Tamanho/Cor do ERP desktop.
// Só edita a seleção: as variações são criadas/removidas pela API ao salvar a ficha.
export default function GradeProduto({ codmer, recarregar, onChange }: GradeProdutoProps) {
  const [grade, setGrade] = useState<IGrade>(gradeVazia);
  const [selecao, setSelecao] = useState<IGradeSelecao>({ cores: [], tamanhos: [] });
  const [coresCadastradas, setCoresCadastradas] = useState<IPadMer[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [novoTamanho, setNovoTamanho] = useState('');
  const [modalCores, setModalCores] = useState(false);
  const [filtroCor, setFiltroCor] = useState('');
  const [coresModal, setCoresModal] = useState<string[]>([]);

  const carregar = async () => {
    try {
      setCarregando(true);
      const [gravada, cores] = await Promise.all([
        codmer ? buscarGrade(codmer) : Promise.resolve(gradeVazia),
        coresCadastradas.length ? Promise.resolve(coresCadastradas) : listarCores()
      ]);
      setGrade(gravada);
      setCoresCadastradas(cores);
      setSelecao({ cores: gravada.cores.map(cor => cor.codpad), tamanhos: gravada.tamanhos });
      onChange(undefined);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar a grade do produto');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [codmer, recarregar]);

  const alterar = (nova: IGradeSelecao) => {
    setSelecao(nova);
    const original = { cores: grade.cores.map(cor => cor.codpad), tamanhos: grade.tamanhos };
    const mudou = !igual(nova.cores, original.cores) || !igual(nova.tamanhos, original.tamanhos);
    onChange(mudou ? nova : undefined);
  };

  const nomeCor = (codpad: string) =>
    coresCadastradas.find(cor => cor.cod === codpad)?.padmer ||
    grade.cores.find(cor => cor.codpad === codpad)?.padmer ||
    codpad;

  const adicionarTamanho = () => {
    const tamanhos = novoTamanho
      .split(',')
      .map(tamanho => tamanho.trim().toUpperCase())
      .filter(Boolean);
    if (!tamanhos.length) return;

    const grandes = tamanhos.filter(tamanho => tamanho.length > TAMANHO_MAXIMO);
    if (grandes.length) {
      toast.warning(`O tamanho pode ter até ${TAMANHO_MAXIMO} caracteres: ${grandes.join(', ')}`);
      return;
    }

    const novos = tamanhos.filter((tamanho, i) => !selecao.tamanhos.includes(tamanho) && tamanhos.indexOf(tamanho) === i);
    if (novos.length) alterar({ ...selecao, tamanhos: [...selecao.tamanhos, ...novos] });
    setNovoTamanho('');
  };

  const moverTamanho = (indice: number, passo: number) => {
    const tamanhos = [...selecao.tamanhos];
    const destino = indice + passo;
    if (destino < 0 || destino >= tamanhos.length) return;
    [tamanhos[indice], tamanhos[destino]] = [tamanhos[destino], tamanhos[indice]];
    alterar({ ...selecao, tamanhos });
  };

  const abrirCores = () => {
    setCoresModal(selecao.cores);
    setFiltroCor('');
    setModalCores(true);
  };

  // Mantém a ordem das cores já escolhidas; as novas entram no fim, na ordem da lista
  const aplicarCores = () => {
    const mantidas = selecao.cores.filter(cor => coresModal.includes(cor));
    const novas = coresCadastradas.map(cor => cor.cod).filter(cor => coresModal.includes(cor) && !mantidas.includes(cor));
    alterar({ ...selecao, cores: [...mantidas, ...novas] });
    setModalCores(false);
  };

  const coresFiltradas = coresCadastradas.filter(cor =>
    `${cor.cod} ${cor.padmer}`.toLowerCase().includes(filtroCor.trim().toLowerCase()));

  const plano = useMemo(() => planejarGrade(grade.variacoes, selecao), [grade, selecao]);
  const linhas = selecao.cores.length ? selecao.cores : [''];
  const colunas = selecao.tamanhos.length ? selecao.tamanhos : [''];
  const temGrade = selecao.cores.length > 0 || selecao.tamanhos.length > 0 || grade.variacoes.length > 1;
  const descricao = (codpad: string, codtam: string) =>
    [codpad && `cor ${nomeCor(codpad)}`, codtam && `tamanho ${codtam}`].filter(Boolean).join(' / ') || 'sem cor e tamanho';

  return (
    <Container>
      <h2>Grade (cor e tamanho)</h2>

      {carregando && <Aviso>Carregando grade...</Aviso>}

      <Bloco>
        <h3>Cores</h3>
        {selecao.cores.length > 0 &&
          <Chips>
            {selecao.cores.map(codpad => (
              <Chip key={codpad}>
                {nomeCor(codpad)}
                <button type='button' aria-label={`Remover a cor ${nomeCor(codpad)}`}
                  onClick={() => alterar({ ...selecao, cores: selecao.cores.filter(cor => cor !== codpad) })}>
                  <FaTimes size={12} />
                </button>
              </Chip>
            ))}
          </Chips>}
        <div>
          <Button variante='secundario' tamanho='sm' icone={<FaPalette />} texto='Escolher cores' onClick={abrirCores} />
        </div>
      </Bloco>

      <Bloco>
        <h3>Tamanhos</h3>
        {selecao.tamanhos.length > 0 &&
          <Chips>
            {selecao.tamanhos.map((tamanho, indice) => (
              <Chip key={tamanho}>
                <button type='button' aria-label={`Mover ${tamanho} para a esquerda`} disabled={indice === 0}
                  onClick={() => moverTamanho(indice, -1)}>
                  <FaArrowLeft size={10} />
                </button>
                {tamanho}
                <button type='button' aria-label={`Mover ${tamanho} para a direita`} disabled={indice === selecao.tamanhos.length - 1}
                  onClick={() => moverTamanho(indice, 1)}>
                  <FaArrowRight size={10} />
                </button>
                <button type='button' aria-label={`Remover o tamanho ${tamanho}`}
                  onClick={() => alterar({ ...selecao, tamanhos: selecao.tamanhos.filter(t => t !== tamanho) })}>
                  <FaTimes size={12} />
                </button>
              </Chip>
            ))}
          </Chips>}
        <LinhaEntrada>
          <FormInput
            placeHolder='Novo tamanho (ex.: P, M, G)'
            value={novoTamanho}
            onChange={e => setNovoTamanho(e.target.value)}
            onKeyDown={e => {
              // Enter não pode enviar a ficha
              if (e.key === 'Enter') {
                e.preventDefault();
                adicionarTamanho();
              }
            }}
          />
          <Button variante='secundario' tamanho='sm' icone={<FaPlus />} texto='Adicionar' onClick={adicionarTamanho} />
        </LinhaEntrada>
      </Bloco>

      {temGrade &&
        <Bloco>
          <h3>Variações</h3>
          <TabelaContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Cor</Th>
                  {colunas.map(codtam => <ColunaTamanho key={codtam || 'sem-tamanho'}>{codtam || 'Sem tamanho'}</ColunaTamanho>)}
                </tr>
              </thead>
              <tbody>
                {linhas.map(codpad => (
                  <tr key={codpad || 'sem-cor'}>
                    <CabecalhoLinha scope='row'>{codpad ? nomeCor(codpad) : 'Sem cor'}</CabecalhoLinha>
                    {colunas.map(codtam => {
                      const chave = chaveVariacao(codpad, codtam);
                      const variacao = plano.existentes.get(chave);
                      if (variacao) {
                        return (
                          <Celula key={chave} $tipo='existente' title={`Código ${variacao.cod}`}>
                            {variacao.estest}
                            {variacao.filtro === 0 && <FaStar className='principal' aria-label='Produto principal' />}
                          </Celula>
                        );
                      }
                      return <Celula key={chave} $tipo='nova'>nova</Celula>;
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TabelaContainer>
          <Legenda>
            <span>Números: estoque de cada variação</span>
            <span><FaStar /> produto principal (o que aparece na lista)</span>
          </Legenda>

          {(plano.novas.size > 0 || plano.removidas.length > 0) &&
            <Resumo $alerta={plano.removidas.length > 0}>
              {'Ao salvar: ' + [
                plano.novas.size > 0 &&
                  `${plano.novas.size} ${plano.novas.size === 1 ? 'variação será criada' : 'variações serão criadas'}`,
                plano.removidas.length > 0 &&
                  `${plano.removidas.length} ${plano.removidas.length === 1 ? 'será removida' : 'serão removidas'} (` +
                  plano.removidas.map(v => descricao(v.codpad, v.codtam)).join('; ') + ')'
              ].filter(Boolean).join(' e ') + '.'}
              {plano.removidas.length > 0 && ' Variações com movimento não podem ser removidas.'}
            </Resumo>}
        </Bloco>}

      {modalCores &&
        <Modal
          titulo='Cores do produto'
          onClose={() => setModalCores(false)}
          rodape={
            <>
              <Button variante='fantasma' texto='Cancelar' onClick={() => setModalCores(false)} />
              <Button texto={`Aplicar (${coresModal.length})`} onClick={aplicarCores} />
            </>
          }
        >
          <FormInput
            placeHolder='Buscar cor'
            value={filtroCor}
            onChange={e => setFiltroCor(e.target.value)}
            // O modal fica dentro do formulário da ficha: Enter não pode salvar
            onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
          />
          <ListaCores>
            {coresFiltradas.length === 0 && <Aviso>Nenhuma cor encontrada.</Aviso>}
            {coresFiltradas.map(cor => (
              <Checkbox
                key={cor.cod}
                label={cor.padmer}
                checked={coresModal.includes(cor.cod)}
                onChange={e => setCoresModal(atuais =>
                  e.target.checked ? [...atuais, cor.cod] : atuais.filter(c => c !== cor.cod))}
              />
            ))}
          </ListaCores>
        </Modal>}
    </Container>
  );
}
